import "./styles/themes.css";
import "./styles/base.css";
import "./styles/orders.css";
import "./styles/components.css";
import "./styles/order-form.css";
import "./styles/stats.css";
import "./styles/owner-orders.css";
import "./styles/session.css";
import "./styles/friend-orders.css";
import "./styles/order-detail.css";

import { SHIPPING_COST, SPLIT_RATE } from "../shared/order-domain";
import { acknowledgeAllItems, ApiError, loadOrders, removeOrder, setItemPrinted, setOrderPrinted } from "./api";
import { initializeDemoDownloads } from "./downloads";
import { closeOrderForm, initOrderForm, openOrderForm } from "./order-form";
import {
  openOrderDetail,
  orderFromEventTarget,
  recordFileDownload,
  renderOrders,
  updateItemInState,
  updateOrderInState,
  updateOrdersLayout,
} from "./orders-view";
import { initSession, restoreSession, showLoginGate } from "./session";
import { isOwner, state, type OrderFilter } from "./state";
import { initStats, renderStatsCards } from "./stats-view";
import { initTheme } from "./theme";
import { initTransfer } from "./transfer-view";
import {
  bindModalBackdrop,
  closeModal,
  element,
  errorMessage,
  hideAllModals,
  openModal,
  setLoading,
  showToast,
} from "./ui";

let refreshTimer: number | null = null;

function render(): void {
  renderStatsCards();
  renderOrders();
}

async function refreshOrders(silent = false): Promise<void> {
  if (!silent) {
    state.loading = true;
    setLoading(true);
  }
  try {
    state.orders = await loadOrders();
    initializeDemoDownloads(state.orders);
    if (!silent) state.loading = false;
    render();
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      stopAutoRefresh();
      showLoginGate();
    }
    if (!silent) throw error;
  } finally {
    if (!silent) {
      state.loading = false;
      setLoading(false);
    }
  }
}

function startAutoRefresh(): void {
  stopAutoRefresh();
  if (!isOwner()) return;
  refreshTimer = window.setInterval(() => {
    if (document.hidden || document.querySelector(".modal-backdrop:not([hidden])")) return;
    void refreshOrders(true);
  }, 30_000);
}

function stopAutoRefresh(): void {
  if (refreshTimer !== null) window.clearInterval(refreshTimer);
  refreshTimer = null;
}

function addOrReplaceOrder(order: (typeof state.orders)[number]): void {
  const index = state.orders.findIndex((candidate) => candidate.id === order.id);
  if (index >= 0) state.orders[index] = order;
  else state.orders.unshift(order);
  render();
}

function initializeOrdersEvents(): void {
  element<HTMLButtonElement>("addBtn").addEventListener("click", () => openOrderForm(null));
  element<HTMLInputElement>("searchInput").addEventListener("input", (event) => {
    state.search = (event.target as HTMLInputElement).value;
    renderOrders();
  });
  element<HTMLElement>("filters").addEventListener("click", (event) => {
    const button = event.target instanceof Element
      ? event.target.closest<HTMLButtonElement>("[data-filter]")
      : null;
    if (!button?.dataset.filter) return;
    document.querySelectorAll(".chip").forEach((chip) => chip.classList.remove("active"));
    button.classList.add("active");
    state.filter = button.dataset.filter as OrderFilter;
    renderOrders();
  });
  element<HTMLButtonElement>("markAllReadBtn").addEventListener("click", async () => {
    const button = element<HTMLButtonElement>("markAllReadBtn");
    button.disabled = true;
    button.textContent = "正在标记…";
    try {
      const result = await acknowledgeAllItems();
      await refreshOrders();
      showToast(`已将 ${result.count} 个文件标为已读`);
    } catch (error) {
      showToast(errorMessage(error, "标为已读失败"), true);
      renderOrders();
    }
  });
  const orders = element<HTMLElement>("ordersMain");
  orders.addEventListener("click", async (event) => {
    recordFileDownload(event);
    const action = event.target instanceof Element
      ? event.target.closest<HTMLButtonElement>("[data-act]")
      : null;
    if (!action) return;
    const order = orderFromEventTarget(action);
    if (!order) return;
    if (action.dataset.act === "edit") openOrderForm(order);
    else if (action.dataset.act === "detail") {
      openOrderDetail(order);
      openModal(element<HTMLElement>("detailModal"));
    } else if (action.dataset.act === "del") {
      const names = order.items.map((item) => item.file_name).filter(Boolean).join("、");
      if (!window.confirm(`确定删除这个订单吗？\n${order.xianyu_id || ""} ${names}`)) return;
      try {
        await removeOrder(order.id);
        state.orders = state.orders.filter((candidate) => candidate.id !== order.id);
        render();
        showToast("订单已删除");
      } catch (error) {
        showToast(errorMessage(error, "删除失败"), true);
      }
    }
  });
  orders.addEventListener("change", async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.classList.contains("status-check") && target.dataset.item) {
      const itemId = Number(target.dataset.item);
      try {
        updateItemInState(await setItemPrinted(itemId, target.checked));
        render();
      } catch (error) {
        target.checked = !target.checked;
        showToast(errorMessage(error, "更新打印状态失败"), true);
      }
    }
    if (target.classList.contains("order-print-check") && target.dataset.order) {
      const orderId = Number(target.dataset.order);
      try {
        updateOrderInState(await setOrderPrinted(orderId, target.checked));
        render();
      } catch (error) {
        target.checked = !target.checked;
        showToast(errorMessage(error, "更新打印状态失败"), true);
      }
    }
  });
}

function initializeCommonUi(): void {
  const detailModal = element<HTMLElement>("detailModal");
  element<HTMLButtonElement>("detailCloseBtn").addEventListener("click", () => closeModal(detailModal));
  bindModalBackdrop(detailModal, () => closeModal(detailModal));
  const scrollHint = element<HTMLButtonElement>("scrollHint");
  scrollHint.addEventListener("click", () =>
    element<HTMLElement>("donePanel").scrollIntoView({ behavior: "smooth", block: "start" }),
  );
  window.addEventListener("scroll", updateOrdersLayout, { passive: true });
  window.addEventListener("resize", updateOrdersLayout);
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    const open = [...document.querySelectorAll<HTMLElement>(".modal-backdrop:not([hidden])")].at(-1);
    if (!open) return;
    if (open.id === "modal") closeOrderForm();
    else closeModal(open);
  });
}

async function boot(): Promise<void> {
  try {
    setLoading(false);
    hideAllModals();
    initTheme();
    element<HTMLElement>("splitFormula").textContent =
      `(总价 − ${SHIPPING_COST} 运费) × ${SPLIT_RATE}`;
    initializeCommonUi();
    initializeOrdersEvents();
    initOrderForm({
      onSaved: addOrReplaceOrder,
      onItemUploaded(_orderId, item) {
        updateItemInState(item);
        render();
      },
      onUploadsSettled: () => refreshOrders(true),
    });
    const transfer = initTransfer({ refresh: () => refreshOrders() });
    initStats({ transfer: transfer.open, undoTransfer: () => void transfer.undo() });
    initSession({
      authenticated: async () => {
        await refreshOrders();
        startAutoRefresh();
      },
      loggedOut: stopAutoRefresh,
    });
    if (!(await restoreSession())) return;
    await refreshOrders();
    startAutoRefresh();
  } catch (error) {
    console.error(error);
    state.loading = false;
    setLoading(false);
    showLoginGate();
    showToast("页面初始化失败，请刷新重试。", true);
  }
}

void boot();
